import CloseIcon from "icons/CloseIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import lockBody from "@Utils/lockBody";
import unlockBody from "@Utils/unLockBody";
import React, { UIEventHandler, useEffect, useRef, useState } from "react";
import { useHistory } from "hooks/useHistory";
import { useLocation } from "react-router-dom";

import styles from "./Modal.module.scss";
import useModal, { getModalsFromState, unwrapState } from "./useModal";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import useAppPadding from "hooks/useAppPadding";
import useIsApp from "hooks/useIsApp";
import useWhitelabel from "hooks/useWhitelabel";

export interface Props {
  hideCloseBtn?: boolean;
  hideModal: any;
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  zIndex?: number;
  transformOrigin?: string;
  hide?: boolean;
  hideCloseButton?: boolean;
  className?: string;
  align?: "center" | "right" | "";
  modalRef?: any;
  onScroll?: UIEventHandler;
  size?:
    | "fitContent"
    | "xlarge"
    | "xlmedium"
    | "large"
    | "thinLarge"
    | "thinSmall"
    | "smallWidth"
    | "medium"
    | "thickSmall"
    | "thinMedium"
    | "xsmall"
    | "small"
    | "autosmall"
    | "xs"
    | "xmedium"
    | "fullScreen"
    | "thinWLargeH"
    | "largeWidth"
    | "loginModalWidth"
    | ""
    | null;
  height?: string;
  isHistoryPush?: boolean;
  type?: string;
  compactWidth?: boolean;
  animation?:
    | "opacity"
    | "bottomToTop"
    | "topToBottom"
    | "rightToLeftAnimation"
    | "noAnimate"
    | ""
    | any;
  noLockBody?: boolean;
  hideOverflow?: boolean;
  keepBodyLocked?: boolean;
  unSetMaxHeight?: boolean;
  renderChildren?: (handleHideModal: any) => JSX.Element;
  noOverlay?: boolean;
  children?: any;
  coverClassName?: string;
  containerClassName?: string;
  outerClickDisabled?: boolean;
  /** Fired when a close begins, before the exit animation reveals the page beneath. */
  onCloseStart?: () => void;
  title?: string;
  hideBackOverlay?: boolean;
  closeBtnClassName?: string;
  closeIconProps?: any;
  queryParams?: any;
  disableEscapeKey?: boolean;
}
const Modal: React.FC<Props> = (props: Props) => {
  const {
    top,
    left,
    right,
    bottom,
    transformOrigin,
    hide,
    hideCloseButton,
    className,
    align,
    modalRef,
    height,
    size,
    isHistoryPush,
    type,
    zIndex,
    compactWidth,
    animation,
    noLockBody,
    hideOverflow,
    keepBodyLocked,
    unSetMaxHeight,
    hideModal,
    children,
    noOverlay,
    renderChildren,
    coverClassName,
    containerClassName,
    outerClickDisabled,
    onCloseStart,
    title,
    onScroll,
    hideBackOverlay,
    hideCloseBtn,
    closeBtnClassName,
    closeIconProps = {},
    queryParams,
    disableEscapeKey = false,
  } = props;
  const [showAnimantion, setShowAnimation] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const shouldBodyLock = useRef(true);
  const appPadding = useAppPadding();
  const isApp = useIsApp();
  const { isWhitelabel } = useWhitelabel();

  const ModalStyles: any = {
    top,
    left,
    right,
    bottom,
    transformOrigin,
    height,
  };

  Object.keys(ModalStyles).forEach((_res, key) => {
    if (!ModalStyles[key]) {
      delete ModalStyles[key];
    }
  });

  const handleKeyPress = (e: any) => {
    if (e.keyCode === 27) {
      // Esc
      if (disableEscapeKey) return;
      handleHideModal(true);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    if (animation === "noAnimate") {
      setShowAnimation(true);
    } else {
      setTimeout(() => {
        setShowAnimation(true);
      }, 50);
    }
    let isModalAlreadyOpen = false;
    if (window.isModalOpen) {
      isModalAlreadyOpen = true;
    } else {
      isModalAlreadyOpen = false;
      window.isModalOpen = true;
    }
    shouldBodyLock.current = !isModalAlreadyOpen && (!noLockBody || !keepBodyLocked);
    if (shouldBodyLock.current) lockBody();
    return () => {
      if (shouldBodyLock.current) unlockBody();
      if (!isModalAlreadyOpen) {
        window.isModalOpen = false;
      }
    };
  }, []);

  // useModal records each history-pushing modal in `_modals` (type-less ones as "").
  const ownsCurrentHistoryEntry = (): boolean =>
    getModalsFromState(unwrapState(window.history?.state)).includes(type || "");

  // Pop only our own entry: `history.length` alone let a stale modal pop the page
  // the user arrived from.
  const handleGoBack = (): void => {
    if (history.length >= 2 && ownsCurrentHistoryEntry()) history.goBack();
  };

  const handleHideModal = (e: any): void => {
    if (outerClickDisabled) return;

    // Before the exit animation below starts revealing the page beneath.
    onCloseStart?.();

    if (size === "fullScreen" || animation === "rightToLeftAnimation") {
      setShowAnimation(false);
      setTimeout(() => {
        if (e && isHistoryPush) {
          handleGoBack(); // goBack() → popstate → handleOnBackClick(null) → hideModal
          return; // ← DON'T call hideModal here too
        }
        if (shouldBodyLock.current) unlockBody();
        hideModal(e);
      }, 300);
    } else {
      if (e && isHistoryPush) {
        handleGoBack(); // same — let popstate handle the rest
        return; // ← DON'T call hideModal here too
      }
      if (shouldBodyLock.current) unlockBody();
      hideModal(e);
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (isHistoryPush) useModal(history, location, type || "", handleHideModal, queryParams);

  const getAlignment = (): string => {
    switch (align) {
      case "center":
        return styles.center;
      case "right":
        return styles.right;
      default:
        return "";
    }
  };

  const getModalSize = (): string => {
    switch (size) {
      case "xs":
        return styles.xs;
      case "xsmall":
        return styles.xsmall;
      case "small":
        return styles.small;
      case "autosmall":
        return styles.autosmall;
      case "thinSmall":
        return styles.thinSmall;
      case "smallWidth":
        return styles.smallWidth;
      case "thickSmall":
        return styles.thickSmall;
      case "thinMedium":
        return styles.thinMedium;
      case "medium":
        return styles.medium;
      case "thinLarge":
        return styles.thinLarge;
      case "xlmedium":
        return styles.xlmedium;
      case "large":
        return styles.large;
      case "xlarge":
        return styles.xlarge;
      case "fullScreen":
        return styles.fullScreen;
      case "fitContent":
        return styles.fitContent;
      case "thinWLargeH":
        return styles.thinWLargeH;
      case "largeWidth":
        return styles.largeWidth;
      case "xmedium":
        return styles.xmedium;
      case "loginModalWidth":
        return styles.loginModalWidth;
      default:
        return "";
    }
  };

  const getAnimation = (): string => {
    switch (animation) {
      case "opacity":
        return styles.opacityAnimation;
      case "bottomToTop":
        return styles.bottomToTopAnimation;
      case "topToBottom":
        return styles.topToBottomAnimation;
      case "rightToLeftAnimation":
        return styles.rightToLeftAnimation;
      case "noAnimate":
        return styles.noAnimate;
      default: {
        if (size === "fullScreen") {
          return styles.rightToLeftAnimation;
        }
        return "";
      }
    }
  };

  const renderTitle = () => {
    if (title) {
      return (
        <div
          className={styles.modalTitle}
          {...(isApp && { style: { paddingTop: `${appPadding}px` } })}
        >
          {!isDesktop && (
            <div
              className={formatClassNames(isWhitelabel ? "wl-title-font" : "", styles.title)}
              data-testid="Login Modal"
            >
              {title}
            </div>
          )}
          {isDesktop && (
            <h4
              className={formatClassNames(isWhitelabel ? "wl-title-font" : "", styles.titleDesktop)}
              data-testid="Login Modal"
            >
              {title}
            </h4>
          )}
          {!hideCloseBtn && (
            <div
              data-testid="property-map-modal-close-icon"
              className={[styles.modalCloseBtn, closeBtnClassName].filter(Boolean).join(" ")}
              role="none"
              onClick={handleHideModal}
            >
              <CloseIcon size={10} {...closeIconProps} />
            </div>
          )}
        </div>
      );
    }
    return (
      <i
        className={[styles.modalCloseBtn, closeBtnClassName].filter(Boolean).join(" ")}
        role="none"
        onClick={handleHideModal}
        data-testid="close-modal-icon"
        {...(isApp && { style: { marginTop: `${appPadding}px` } })}
      >
        <CloseIcon size={16} strokeWidth={2} {...closeIconProps} />
      </i>
    );
  };

  return (
    <div
      className={formatClassNames(styles.modalOverlay, containerClassName)}
      style={zIndex ? { zIndex } : {}}
    >
      {size !== "fullScreen" && !hideBackOverlay && (
        <div
          role="none"
          className={formatClassNames(
            styles.modalOuterContainer,
            noOverlay && styles.noOverlay,
            !hide && styles.show,
            animation === "opacity" && styles.animate,
            coverClassName,
          )}
          onClick={handleHideModal}
        />
      )}
      <div
        className={[
          styles.modalOverlayContainer,
          !hide && showAnimantion && styles.show,
          className && className,
          compactWidth && styles.compactWidth,
          hideOverflow && styles.hideOverflow,
          unSetMaxHeight && styles.unSetMaxHeight,
          getAlignment(),
          getModalSize(),
          getAnimation(),
        ]
          .filter(Boolean)
          .join(" ")}
        ref={modalRef}
        style={{ ...ModalStyles, ...(isApp && !title && { paddingTop: `${appPadding}px` }) }}
        onScroll={onScroll}
      >
        {!hideCloseButton && renderTitle()}
        {children}
        {renderChildren && renderChildren(handleHideModal)}
      </div>
    </div>
  );
};

Modal.defaultProps = {
  top: "",
  left: "",
  right: "",
  bottom: "",
  zIndex: 0,
  transformOrigin: "",
  hide: false,
  hideCloseButton: false,
  className: "",
  align: "",
  modalRef: null,
  size: "fullScreen",
  height: "",
  isHistoryPush: false,
  type: "",
  compactWidth: false,
  animation: "",
  noLockBody: false,
  hideOverflow: false,
  keepBodyLocked: false,
  unSetMaxHeight: false,
  renderChildren: undefined,
  noOverlay: false,
  children: <></>,
  coverClassName: "",
  containerClassName: "",
  outerClickDisabled: false,
  onCloseStart: undefined,
  title: "",
  closeBtnClassName: "",
  hideCloseBtn: false,
  disableEscapeKey: false,
};

export default wrapperHOC(Modal, {
  componentName: "Modal-Global",
  showForChina: true,
});
